import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, Building2, GraduationCap, UserCheck } from 'lucide-react'
import toast from 'react-hot-toast'
import api, { getApiError } from '../services/api'
import { useAuthStore } from '../stores/authStore'

const initialStudent = { nome: '', email: '', senha: '', nivel_escolar: '' }
const initialTeacher = { nome: '', email: '', senha: '' }
const initialInstitution = {
  nome_responsavel: '',
  email: '',
  senha: '',
  nome_instituicao: '',
  tipo_instituicao: '',
  cnpj: '',
  telefone: ''
}

function RegisterPage() {
  const navigate = useNavigate()
  const setSession = useAuthStore(state => state.setSession)
  const [tipoCadastro, setTipoCadastro] = useState(null)
  const [student, setStudent] = useState(initialStudent)
  const [teacher, setTeacher] = useState(initialTeacher)
  const [institution, setInstitution] = useState(initialInstitution)
  const [loading, setLoading] = useState(false)

  const submitStudent = async event => {
    event.preventDefault()
    setLoading(true)
    try {
      const { data } = await api.post('/auth/register', { ...student, tipo_perfil: 'aluno' })
      setSession(data)
      toast.success('Conta criada com sucesso!')
      navigate('/onboarding')
    } catch (error) {
      toast.error(getApiError(error))
    } finally {
      setLoading(false)
    }
  }

  const submitInstitution = async event => {
    event.preventDefault()
    setLoading(true)
    try {
      const { data } = await api.post('/auth/register/instituicao', institution)
      setSession(data)
      toast.success('Instituição enviada para aprovação!', { duration: 5000 })
      navigate('/instituicao')
    } catch (error) {
      toast.error(getApiError(error))
    } finally {
      setLoading(false)
    }
  }

  const submitTeacher = async event => {
    event.preventDefault()
    setLoading(true)
    try {
      await api.post('/auth/register', { ...teacher, tipo_perfil: 'professor' })
      toast.success('Cadastro de professor enviado para aprovação!', { duration: 5000 })
      setTipoCadastro('professor-enviado')
    } catch (error) {
      toast.error(getApiError(error))
    } finally {
      setLoading(false)
    }
  }

  const updateStudent = (field, value) => setStudent(current => ({ ...current, [field]: value }))
  const updateTeacher = (field, value) => setTeacher(current => ({ ...current, [field]: value }))
  const updateInstitution = (field, value) => setInstitution(current => ({ ...current, [field]: value }))

  return (
    <div className="min-h-screen bg-white px-6 py-10 text-gray-900 transition-colors dark:bg-zinc-950 dark:text-zinc-100">
      <div className="mx-auto flex min-h-[calc(100vh-5rem)] w-full max-w-3xl flex-col items-center justify-center">
        <img
          src="/brand/agente-de-estudo-icone.svg"
          alt="Agente de Estudo"
          width="48"
          height="48"
          className="mb-6 h-12 w-12"
        />

        {!tipoCadastro && (
          <div className="w-full text-center">
            <h1 className="text-2xl font-medium">Como você quer começar?</h1>
            <p className="mt-2 text-sm text-gray-500 dark:text-zinc-400">Escolha o tipo de cadastro que melhor representa você.</p>

            <div className="mx-auto mt-8 grid max-w-3xl gap-4 sm:grid-cols-3">
              <button
                type="button"
                onClick={() => setTipoCadastro('aluno')}
                className="card group flex min-h-48 flex-col items-start text-left transition hover:-translate-y-0.5 hover:border-black dark:hover:border-white"
              >
                <span className="mb-5 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gray-100 dark:bg-zinc-800">
                  <GraduationCap size={22} />
                </span>
                <strong className="text-base font-medium">Quero estudar</strong>
                <span className="mt-2 text-sm leading-6 text-gray-500 dark:text-zinc-400">Conta individual para pesquisar aulas, usar o caderno e organizar revisões.</span>
              </button>

              <button
                type="button"
                onClick={() => setTipoCadastro('professor')}
                className="card group flex min-h-48 flex-col items-start text-left transition hover:-translate-y-0.5 hover:border-black dark:hover:border-white"
              >
                <span className="mb-5 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gray-100 dark:bg-zinc-800">
                  <UserCheck size={22} />
                </span>
                <strong className="text-base font-medium">Sou professor</strong>
                <span className="mt-2 text-sm leading-6 text-gray-500 dark:text-zinc-400">Crie seu perfil para participar de instituições, turmas e recomendar aulas.</span>
              </button>

              <button
                type="button"
                onClick={() => setTipoCadastro('instituicao')}
                className="card group flex min-h-48 flex-col items-start text-left transition hover:-translate-y-0.5 hover:border-black dark:hover:border-white"
              >
                <span className="mb-5 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gray-100 dark:bg-zinc-800">
                  <Building2 size={22} />
                </span>
                <strong className="text-base font-medium">Cadastrar instituição</strong>
                <span className="mt-2 text-sm leading-6 text-gray-500 dark:text-zinc-400">Para escolas, faculdades, cursos, preparatórios e projetos educacionais.</span>
              </button>
            </div>
          </div>
        )}

        {tipoCadastro === 'professor-enviado' ? (
          <div className="card w-full max-w-lg text-center">
            <span className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
              <UserCheck size={24} />
            </span>
            <h1 className="text-xl font-medium">Cadastro recebido</h1>
            <p className="mt-2 text-sm leading-6 text-gray-500 dark:text-zinc-400">Avisamos o administrador. Assim que seu perfil for aprovado e você estiver vinculado a uma turma, poderá recomendar links importantes aos seus alunos.</p>
            <Link to="/login" className="btn-primary mt-6 inline-flex px-6 py-2.5">Ir para o login</Link>
          </div>
        ) : tipoCadastro && (
          <div className="w-full max-w-lg">
            <button type="button" onClick={() => setTipoCadastro(null)} className="mb-5 inline-flex items-center gap-2 text-sm text-gray-500 hover:text-black dark:text-zinc-400 dark:hover:text-white">
              <ArrowLeft size={16} /> Voltar às opções
            </button>
            <h1 className="text-xl font-medium">{tipoCadastro === 'aluno' ? 'Criar conta individual' : tipoCadastro === 'professor' ? 'Criar perfil de professor' : 'Cadastrar instituição'}</h1>
            <p className="mb-7 mt-1 text-sm text-gray-500 dark:text-zinc-400">
              {tipoCadastro === 'aluno' ? 'Comece sua jornada de estudos.' : tipoCadastro === 'professor' ? 'Envie seus dados para aprovação do administrador.' : 'Crie sua conta de responsável e envie a instituição para aprovação.'}
            </p>

            {tipoCadastro === 'aluno' ? (
              <form onSubmit={submitStudent} className="space-y-3">
                <input className="input-field" type="text" value={student.nome} onChange={event => updateStudent('nome', event.target.value)} placeholder="Nome completo" autoComplete="name" required />
                <input className="input-field" type="email" value={student.email} onChange={event => updateStudent('email', event.target.value)} placeholder="E-mail" autoComplete="email" required />
                <input className="input-field" type="password" value={student.senha} onChange={event => updateStudent('senha', event.target.value)} placeholder="Senha (mín. 6 caracteres)" autoComplete="new-password" minLength={6} required />
                <select className="input-field" value={student.nivel_escolar} onChange={event => updateStudent('nivel_escolar', event.target.value)} required>
                  <option value="">Selecione seu nível de escolaridade</option>
                  <option value="fundamental_1">Fundamental I (1º ao 5º ano)</option>
                  <option value="fundamental_2">Fundamental II (6º ao 9º ano)</option>
                  <option value="ensino_medio">Ensino Médio</option>
                  <option value="pre_vestibular">Pré-vestibular / ENEM</option>
                  <option value="ensino_superior">Ensino Superior</option>
                  <option value="eja">EJA</option>
                  <option value="estudo_livre">Estudo livre</option>
                </select>
                <button type="submit" disabled={loading} className="btn-primary w-full py-2.5">{loading ? 'Criando conta...' : 'Criar conta'}</button>
              </form>
            ) : tipoCadastro === 'professor' ? (
              <form onSubmit={submitTeacher} className="space-y-3">
                <input className="input-field" type="text" value={teacher.nome} onChange={event => updateTeacher('nome', event.target.value)} placeholder="Nome completo" autoComplete="name" required />
                <input className="input-field" type="email" value={teacher.email} onChange={event => updateTeacher('email', event.target.value)} placeholder="E-mail profissional ou pessoal" autoComplete="email" required />
                <input className="input-field" type="password" value={teacher.senha} onChange={event => updateTeacher('senha', event.target.value)} placeholder="Senha (mín. 6 caracteres)" autoComplete="new-password" minLength={6} required />
                <p className="rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">Por segurança, o acesso como professor será liberado após uma análise rápida do administrador.</p>
                <button type="submit" disabled={loading} className="btn-primary w-full py-2.5">{loading ? 'Enviando cadastro...' : 'Enviar cadastro de professor'}</button>
              </form>
            ) : (
              <form onSubmit={submitInstitution} className="space-y-3">
                <input className="input-field" type="text" value={institution.nome_responsavel} onChange={event => updateInstitution('nome_responsavel', event.target.value)} placeholder="Nome completo do responsável" autoComplete="name" required />
                <input className="input-field" type="email" value={institution.email} onChange={event => updateInstitution('email', event.target.value)} placeholder="E-mail do responsável" autoComplete="email" required />
                <input className="input-field" type="password" value={institution.senha} onChange={event => updateInstitution('senha', event.target.value)} placeholder="Senha (mín. 6 caracteres)" autoComplete="new-password" minLength={6} required />
                <div className="my-5 border-t border-gray-100 dark:border-zinc-800" />
                <input className="input-field" type="text" value={institution.nome_instituicao} onChange={event => updateInstitution('nome_instituicao', event.target.value)} placeholder="Nome da instituição" required />
                <select className="input-field" value={institution.tipo_instituicao} onChange={event => updateInstitution('tipo_instituicao', event.target.value)} required>
                  <option value="">Tipo de instituição</option>
                  <option value="escola">Escola</option>
                  <option value="faculdade">Faculdade</option>
                  <option value="curso">Curso</option>
                  <option value="preparatorio">Preparatório</option>
                  <option value="projeto_social">Projeto social</option>
                  <option value="outro">Outro</option>
                </select>
                <div className="grid gap-3 sm:grid-cols-2">
                  <input className="input-field" type="text" value={institution.cnpj} onChange={event => updateInstitution('cnpj', event.target.value)} placeholder="CNPJ (opcional)" inputMode="numeric" />
                  <input className="input-field" type="tel" value={institution.telefone} onChange={event => updateInstitution('telefone', event.target.value)} placeholder="Telefone (opcional)" autoComplete="tel" />
                </div>
                <p className="rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">O responsável já poderá estudar. Os recursos institucionais serão liberados após a aprovação do administrador.</p>
                <button type="submit" disabled={loading} className="btn-primary w-full py-2.5">{loading ? 'Enviando cadastro...' : 'Cadastrar instituição'}</button>
              </form>
            )}
          </div>
        )}

        <p className="mt-6 text-sm text-gray-500 dark:text-zinc-400">Já tem conta? <Link to="/login" className="font-medium text-black underline dark:text-white">Entrar</Link></p>
      </div>
    </div>
  )
}

export default RegisterPage
